/**
 * PoolPage — "Misc → Pool & Spa": the AquaAI Pool & Spa Advisor ported
 * into ReefApp. All data (test logs, volumes, saved AI plans, weekly
 * guides) persists on the Pi via `/api/userdata` (`pool-*` namespaces)
 * instead of Google Sheets / browser localStorage.
 */
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { useServerBackedState } from '../hooks/useServerBackedState';
import { TabContent } from '../pool/components/TabContent';
import type {
  AIActionPlanStep,
  TestLogEntry,
  TestResult,
  WaterBodyType,
} from '../pool/types';
import { WATER_BODIES } from '../pool/types';
import { generateAIActionPlan } from '../pool/services/geminiService';
import type { WeeklyDosingGuide } from '../pool/services/geminiService';

// ─── Saved-on-Pi record shapes ───────────────────────────────────────────────

export interface SavedAiPlan {
  id: number;
  timestamp: string;
  waterBody: WaterBodyType;
  poolVolume: number;
  plan: AIActionPlanStep[];
}

export interface SavedWeeklyGuide {
  id: number;
  timestamp: string;
  waterBody: WaterBodyType;
  guide: WeeklyDosingGuide;
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'new_test', label: 'New Test' },
  { id: 'action_plan', label: 'Action Plan' },
  { id: 'ai_action_plan', label: 'AI Action Plan' },
  { id: 'weekly_dosing', label: 'Weekly Dosing' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'lsi', label: 'Water Balance' },
  { id: 'log_charts', label: 'Log & Charts' },
  { id: 'chem_101', label: 'Chemistry 101' },
  { id: 'product_guide', label: 'Product Guide' },
];

function TabButton({
  tabId,
  label,
  activeTab,
  setActiveTab,
}: {
  tabId: string;
  label: string;
  activeTab: string;
  setActiveTab: (t: string) => void;
}) {
  return (
    <button
      onClick={() => setActiveTab(tabId)}
      className={`flex-1 text-center py-2 px-3 rounded-md text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
        activeTab === tabId
          ? 'bg-cyan-500 text-white shadow-md'
          : 'text-slate-300 hover:bg-slate-700/50'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function PoolPage() {
  const [activeTab, setActiveTab] = useState('new_test');

  // Server-backed (Pi) state — replaces Google Sheets + localStorage.
  const waterBodyState = useServerBackedState<WaterBodyType>('pool-waterBody', 'pool');
  const poolLogState = useServerBackedState<TestLogEntry[]>('pool-poolTestLog', []);
  const hotTubLogState = useServerBackedState<TestLogEntry[]>('pool-hotTubTestLog', []);
  const poolVolumeState = useServerBackedState<number>('pool-poolVolume', 14600);
  const hotTubVolumeState = useServerBackedState<number>('pool-hotTubVolume', 400);
  const savedPlansState = useServerBackedState<SavedAiPlan[]>('pool-aiPlans', []);
  const savedGuidesState = useServerBackedState<SavedWeeklyGuide[]>('pool-weeklyGuides', []);

  const waterBody = waterBodyState.value;
  const isPool = waterBody === 'pool';
  const log: TestLogEntry[] = isPool ? poolLogState.value : hotTubLogState.value;
  const logState = isPool ? poolLogState : hotTubLogState;
  const volume: number = isPool ? poolVolumeState.value : hotTubVolumeState.value;
  const volumeState = isPool ? poolVolumeState : hotTubVolumeState;

  // Adapter: TabContent expects a React.Dispatch-style setter with
  // functional-update support; ServerBackedState.setValue takes plain T.
  const setLog: Dispatch<SetStateAction<TestLogEntry[]>> = useCallback(
    (action) => {
      const prev = logState.value;
      const next = typeof action === 'function' ? action(prev) : action;
      logState.setValue(next);
    },
    [logState],
  );
  const setVolume = useCallback(
    (v: number) => volumeState.setValue(v),
    [volumeState],
  );

  const [currentAIPlan, setCurrentAIPlan] = useState<AIActionPlanStep[] | null>(null);
  const [isLoadingAIPlan, setIsLoadingAIPlan] = useState(false);
  const [errorAIPlan, setErrorAIPlan] = useState<string | null>(null);
  const [latestTestResult, setLatestTestResult] = useState<TestResult | null>(null);
  const [expandedGuideId, setExpandedGuideId] = useState<number | null>(null);

  // Once the active log finishes loading from the Pi, seed the
  // "latest test result" used by the Action Plan / Water Balance tabs.
  useEffect(() => {
    if (logState.loading) return;
    setLatestTestResult(
      log.length > 0 ? ([...log].sort((a, b) => b.id - a.id)[0] ?? null) : null,
    );
  }, [logState.loading, waterBody, log]);

  const handleSwitchWaterBody = useCallback(
    (newBody: WaterBodyType) => {
      waterBodyState.setValue(newBody);
      const target: TestLogEntry[] =
        newBody === 'pool' ? poolLogState.value : hotTubLogState.value;
      setLatestTestResult(
        target.length > 0
          ? ([...target].sort((a, b) => b.id - a.id)[0] ?? null)
          : null,
      );
      setCurrentAIPlan(null);
      setErrorAIPlan(null);
    },
    [waterBodyState, poolLogState.value, hotTubLogState.value],
  );

  const runAiPlan = useCallback(
    (results: TestResult | TestLogEntry, history: TestLogEntry[]) => {
      if (volume <= 0) return;
      setIsLoadingAIPlan(true);
      setErrorAIPlan(null);
      setCurrentAIPlan(null);
      generateAIActionPlan(results, volume, waterBody, history)
        .then((plan) => setCurrentAIPlan(plan))
        .catch((e: unknown) =>
          setErrorAIPlan(e instanceof Error ? e.message : 'Unknown error'),
        )
        .finally(() => setIsLoadingAIPlan(false));
    },
    [volume, waterBody],
  );

  const handleSaveTest = useCallback(
    (results: TestResult) => {
      const newEntry: TestLogEntry = {
        ...results,
        id: Date.now(),
        timestamp: new Date().toISOString(),
      };
      const withNew = [...log, newEntry];
      logState.setValue(withNew);
      setLatestTestResult(results);
      setActiveTab('action_plan');
      runAiPlan(results, withNew);
    },
    [log, logState, runAiPlan],
  );

  const handleDeleteLog = useCallback(
    (id: number) => {
      logState.setValue(log.filter((e) => e.id !== id));
    },
    [log, logState],
  );

  const handleUpdateLogEntry = useCallback(
    (updated: TestLogEntry) => {
      logState.setValue(log.map((e) => (e.id === updated.id ? updated : e)));
      if (
        latestTestResult &&
        'id' in latestTestResult &&
        (latestTestResult as TestLogEntry).id === updated.id
      ) {
        setLatestTestResult(updated);
      }
    },
    [log, logState, latestTestResult],
  );

  const handleRerunAnalysis = useCallback(
    (entry: TestLogEntry, targetTab: 'ai_action_plan' | 'action_plan') => {
      setLatestTestResult(entry);
      setActiveTab(targetTab);
      if (targetTab === 'ai_action_plan') runAiPlan(entry, log);
    },
    [log, runAiPlan],
  );

  const handleSaveAIPlan = useCallback(
    async (plan: AIActionPlanStep[]) => {
      const entry: SavedAiPlan = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        waterBody,
        poolVolume: volume,
        plan,
      };
      savedPlansState.setValue([...savedPlansState.value, entry]);
      await Promise.resolve();
    },
    [savedPlansState, waterBody, volume],
  );

  const handleSaveWeeklyGuide = useCallback(
    async (guide: WeeklyDosingGuide) => {
      const entry: SavedWeeklyGuide = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        waterBody,
        guide,
      };
      savedGuidesState.setValue([...savedGuidesState.value, entry]);
      await Promise.resolve();
    },
    [savedGuidesState, waterBody],
  );

  const activeBodyDef = WATER_BODIES.find((b) => b.id === waterBody) ?? {
    id: waterBody,
    label: isPool ? 'Pool' : 'Hot Tub / Spa',
    icon: isPool ? '🏊' : '🛁',
  };

  // Pi-storage status line (replaces the old Google Sheets settings card).
  const storageError = poolLogState.error ?? hotTubLogState.error;
  const savedPlansForBody = savedPlansState.value.filter((p) => p.waterBody === waterBody);
  const savedGuidesForBody = savedGuidesState.value.filter((g) => g.waterBody === waterBody);

  return (
    <div className="bg-slate-900 text-white min-h-screen font-sans">
      <div className="container mx-auto p-4 max-w-4xl">
        <header className="text-center my-6">
          <h1 className="text-4xl font-bold text-cyan-400">Pool &amp; Spa Advisor</h1>
          <p className="text-slate-400 mt-2">
            Your complete guide to perfect water chemistry, powered by AI.
          </p>
          <div className="mt-4 flex justify-center">
            <div className="flex bg-slate-800 rounded-xl p-1 gap-1 shadow-lg">
              {WATER_BODIES.map((body) => (
                <button
                  key={body.id}
                  onClick={() => handleSwitchWaterBody(body.id)}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                    waterBody === body.id
                      ? 'bg-cyan-500 text-white shadow-md'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <span className="text-lg">{body.icon}</span>
                  {body.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-2">
            Currently viewing:{' '}
            <span className="text-cyan-400 font-semibold">
              {activeBodyDef.icon} {activeBodyDef.label}
            </span>
          </p>
        </header>

        <nav className="flex flex-wrap justify-center bg-slate-800 rounded-lg p-1 mb-6 shadow-lg gap-1">
          {TABS.map((tab) => (
            <TabButton
              key={tab.id}
              tabId={tab.id}
              label={tab.label}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          ))}
        </nav>

        <main>
          <TabContent
            activeTab={activeTab}
            waterBody={waterBody}
            volume={volume}
            setVolume={setVolume}
            log={log}
            setLog={setLog}
            latestTestResult={latestTestResult}
            currentAIPlan={currentAIPlan}
            isLoadingAIPlan={isLoadingAIPlan}
            errorAIPlan={errorAIPlan}
            onSaveTest={handleSaveTest}
            onRerunAnalysis={handleRerunAnalysis}
            onUpdateLogEntry={handleUpdateLogEntry}
            onDeleteLogEntry={handleDeleteLog}
            onSaveAIPlan={handleSaveAIPlan}
            onSaveWeeklyGuide={handleSaveWeeklyGuide}
          />
        </main>

        {/* Saved AI plans (stored on the Pi) */}
        {activeTab === 'ai_action_plan' && savedPlansForBody.length > 0 && (
          <section className="mt-6 bg-slate-800 p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">
              Saved AI plans on the Pi ({savedPlansForBody.length})
            </h3>
            <ul className="space-y-2">
              {[...savedPlansForBody].reverse().map((saved) => (
                <li
                  key={saved.id}
                  className="flex items-center justify-between bg-slate-700/50 rounded-md px-3 py-2 text-sm"
                >
                  <span className="text-slate-300">
                    {new Date(saved.timestamp).toLocaleString()} —{' '}
                    {saved.plan.length} step{saved.plan.length === 1 ? '' : 's'} (
                    {saved.poolVolume.toLocaleString()} gal)
                  </span>
                  <span className="flex gap-2">
                    <button
                      onClick={() => setCurrentAIPlan(saved.plan)}
                      className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold"
                    >
                      View
                    </button>
                    <button
                      onClick={() =>
                        savedPlansState.setValue(
                          savedPlansState.value.filter((p) => p.id !== saved.id),
                        )
                      }
                      className="px-3 py-1 rounded bg-red-700 hover:bg-red-600 text-white text-xs font-semibold"
                    >
                      Delete
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Saved weekly guides (stored on the Pi) */}
        {activeTab === 'weekly_dosing' && savedGuidesForBody.length > 0 && (
          <section className="mt-6 bg-slate-800 p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-cyan-400 mb-3">
              Saved weekly guides on the Pi ({savedGuidesForBody.length})
            </h3>
            <ul className="space-y-2">
              {[...savedGuidesForBody].reverse().map((saved) => (
                <li key={saved.id} className="bg-slate-700/50 rounded-md px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">
                      {new Date(saved.timestamp).toLocaleString()} — week of{' '}
                      {saved.guide.weekStarting}
                    </span>
                    <span className="flex gap-2">
                      <button
                        onClick={() =>
                          setExpandedGuideId(
                            expandedGuideId === saved.id ? null : saved.id,
                          )
                        }
                        className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold"
                      >
                        {expandedGuideId === saved.id ? 'Hide' : 'View'}
                      </button>
                      <button
                        onClick={() =>
                          savedGuidesState.setValue(
                            savedGuidesState.value.filter((g) => g.id !== saved.id),
                          )
                        }
                        className="px-3 py-1 rounded bg-red-700 hover:bg-red-600 text-white text-xs font-semibold"
                      >
                        Delete
                      </button>
                    </span>
                  </div>
                  {expandedGuideId === saved.id && (
                    <div className="mt-3 space-y-3 text-slate-300">
                      <p className="italic">{saved.guide.summary}</p>
                      {saved.guide.weeklyGoals.length > 0 && (
                        <ul className="list-disc list-inside text-xs text-slate-400">
                          {saved.guide.weeklyGoals.map((goal, i) => (
                            <li key={i}>{goal}</li>
                          ))}
                        </ul>
                      )}
                      {saved.guide.days.map((day) => (
                        <div key={day.day} className="border-t border-slate-600 pt-2">
                          <p className="font-semibold text-cyan-300">{day.day}</p>
                          {day.tasks.length > 0 && (
                            <p className="text-xs">Tasks: {day.tasks.join('; ')}</p>
                          )}
                          {day.chemicals.map((chem, i) => (
                            <p key={i} className="text-xs">
                              <span className="font-semibold">{chem.name}</span> —{' '}
                              {chem.dose}. {chem.instructions}
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-8 space-y-4">
          <div className="bg-slate-800 p-4 rounded-lg shadow-md text-center text-xs">
            {storageError ? (
              <p className="text-amber-400">
                ⚠ Pi unreachable — changes are kept in this browser and the Pi copy
                may be stale. ({storageError})
              </p>
            ) : (
              <p className="text-slate-400">
                ✓ All pool &amp; spa data is stored on the Pi (survives deploys, works
                from any device).
              </p>
            )}
          </div>
          <div className="bg-slate-800 p-4 rounded-lg shadow-md">
            <footer className="text-center text-xs text-slate-200">
              <p className="mb-2">
                Disclaimer: This tool provides recommendations based on the data you
                provide. You use this information at your own risk. Always follow
                chemical manufacturer&apos;s instructions for handling, dosage, and
                safety. The developer is not responsible for any damage to your pool
                or equipment.
              </p>
              <p>Copyright © {new Date().getFullYear()} Josh Cole.</p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
